import { useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { useTranslation } from 'react-i18next'

import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'

import {
  useFetchTicketStatusesChartDataQuery,
  useFetchBondedUnbondedTicketsChartDataQuery,
  useFetchRecentBondedUnbondedTicketsCountQuery,
  useFetchRecentNewClosedTicketsCountQuery,
  useFetchLastRequestStatusesQuery,
} from 'api/dashboard'

import * as ticketingSystemsSelector from 'selectors/ticketingSystems'
import * as sessionUserSelector from 'selectors/sessionUser'

import CardsListPanel from 'components/Shared/CardsListPanel/Index'

import TimeIntervalsPanel from './TimeIntervalsPanel'
import StatsPanel from './StatsPanel/Index'
import ApiRequestsTable from './ApiRequestsTable/Index'
import TicketsTable from './TicketsTable'
import ResponseCodesChart from './ResponseCodesChart'
import TicketStatusesChart from './TicketStatusesChart'
import BondedUnbondedCharts from './BondedUnbondedCharts/Index'
import HttpRequestCodeChart from './HttpRequestCodeChart/Index'
import CreatedClosedChart from './CreatedClosedChart/Index'
import LastSubscriptionsTable from './LastSubscriptionsTable'
import LastRequestStatusTable from './LastRequestStatusTable'

import { shortAbbr } from 'utils/time'
import colors from 'components/utils/colors'

import { colorizeChartSeries } from 'utils/color'

import {
  DASHBOARD_TIME_INTERVAL_SUMMARY,
} from 'constants/constants'

import cloneDeep from 'lodash/cloneDeep'

const ChartsRow = styled.div`
  display: flex;
  min-height: 270px;
  background-color: ${colors.secondary95};
  padding: 0 16px 8px 16px;
`

const SlimChartRow = styled(ChartsRow)`
  min-height: 208px;
`

const Spacer = styled.div`
  width: 8px;
  min-width: 8px;
`

const StyledTableDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 8px;
`

const SpacerHeight = styled.div`
  height: 8px;
`
const DivBlock = styled.div`
  margin-right: -8px;
  display: flex;
  position: relative;
  flex-wrap: wrap;
  flex: 1;

`
const StatusBlocks = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: row;
  padding-bottom: 8px;
`
const CartDiv = styled.div`
  display: flex;
  width: 49.8%;
`

const ChartsWrapper = styled.div`
  background-color: ${colors.secondary95};
  padding-bottom: 8px
`

const DashboardPage = () => {
  // Use localStore here as fix for https://ccp.sys.comcast.net/browse/RELAY-1128

  const userTimeZone = useSelector(sessionUserSelector.timezone)
  const userTimeFormat24 = useSelector(sessionUserSelector.timeFormat24)
  const mainUserPartnerId = useSelector(sessionUserSelector.mainUserPartnerId)
  const isAnyAdmin = useSelector(sessionUserSelector.isAnyAdmin)

  let ticketingSystemList = useSelector(ticketingSystemsSelector.ticketingSystemsWithApiAccess)
  if (isAnyAdmin) {
    ticketingSystemList = ticketingSystemList.filter(ts => ts.partnerId === mainUserPartnerId.toString())
  }

  const [selectedTicketingSystem, setSelectedTicketingSystem] = useState(ticketingSystemList[0])
  const [selectedTimeIntervalId, setSelectedTimeIntervalId] = useState(DASHBOARD_TIME_INTERVAL_SUMMARY)

  const selectedTicketingSystemDisplayId = selectedTicketingSystem?.displayId
  const userTimeZoneAbbr = shortAbbr(userTimeZone)

  const isSummaryTab = selectedTimeIntervalId === DASHBOARD_TIME_INTERVAL_SUMMARY

  const onTicketingSystemSelected = (ticketingSystem) => {
    setSelectedTicketingSystem(ticketingSystem)
  }

  const onTimeIntervalSelected = (timeInterval) => {
    setSelectedTimeIntervalId(timeInterval.id)
  }

  const { t } = useTranslation()

  const fetchLastRequestStatuses =
    useFetchLastRequestStatusesQuery({
      ticketingSystemDisplayId: selectedTicketingSystemDisplayId,
    },
    {
      skip: !isSummaryTab,
    })

  const [ticketLastRequestStatuses, commentLastRequestStatuses, attachmentLastRequestStatuses, fetchingLastRequestStatuses] = [
    fetchLastRequestStatuses?.data?.ticketsRequest || [],
    fetchLastRequestStatuses?.data?.commentsRequest || [],
    fetchLastRequestStatuses?.data?.attachmentsRequest || [],
    fetchLastRequestStatuses?.isFetching,
  ]


  const requestBondedUnbondedTicketsChartData =
    useFetchBondedUnbondedTicketsChartDataQuery({
      ticketingSystemDisplayId: selectedTicketingSystemDisplayId,
      timeInterval: selectedTimeIntervalId,
    },
    {
      skip: isSummaryTab,
    })

  const [bondedUnbondedTickets, fetchingBondedUnbonded] =
    [
      requestBondedUnbondedTicketsChartData?.data?.bondedUnbondedTickets || [],
      requestBondedUnbondedTicketsChartData.isFetching,
    ]

  const colorizedBondedUnbondedTickets = colorizeChartSeries(cloneDeep(bondedUnbondedTickets))

  const requestRecentBondedUnbondedTicketsCount =
    useFetchRecentBondedUnbondedTicketsCountQuery({
      ticketingSystemDisplayId: selectedTicketingSystemDisplayId,
    },
    {
      skip: !isSummaryTab,
    })

  const [recentBondedTicketsCount, recentUnbondedTicketsCount, fetchingRecentBondedUnbonded] =
    [
      requestRecentBondedUnbondedTicketsCount?.data?.recentBondedTicketsCount,
      requestRecentBondedUnbondedTicketsCount?.data?.recentUnbondedTicketsCount,
      requestRecentBondedUnbondedTicketsCount.isFetching,
    ]

  const requestRecentNewClosedTicketsCount =
    useFetchRecentNewClosedTicketsCountQuery({
      ticketingSystemDisplayId: selectedTicketingSystemDisplayId,
    },
    {
      skip: !isSummaryTab,
    })

  const [recentNewTicketsCount, recentClosedTicketsCount, fetchingRecentNewClosed] =
    [
      requestRecentNewClosedTicketsCount?.data?.newTicketsCount,
      requestRecentNewClosedTicketsCount?.data?.closedTicketsCount,
      requestRecentNewClosedTicketsCount.isFetching,
    ]

  const requestTicketStatusesChartData =
    useFetchTicketStatusesChartDataQuery({
      ticketingSystemDisplayId: selectedTicketingSystemDisplayId,
      timeInterval: selectedTimeIntervalId,
    },
    {
      skip: isSummaryTab,
    })

  const [ticketStatuses, fetchingOpenClosed] = [
    requestTicketStatusesChartData?.data?.ticketStatuses || [],
    requestTicketStatusesChartData.isFetching,
  ]

  const colorizedTicketStatuses = colorizeChartSeries(cloneDeep(ticketStatuses))

  const showAttachmentsData = selectedTicketingSystem && selectedTicketingSystem.attachmentsEnabled


  const getTicketActionList = (items, type) => {
    const actionList = items ? (type ? items.slice(0, 3) : items.slice(3, 6)) : {}
    const renderList = type
      ? [
          { 'action': t('dashboardPage.createAction'), 'time': actionList[0] ? actionList[0].ticketCreateSuccess : 'no data' },
          { 'action': t('dashboardPage.updateAction'), 'time': actionList[1] ? actionList[1].ticketUpdateSuccess : 'no data' },
          { 'action': t('dashboardPage.collectionAction'), 'time': actionList[2] ? actionList[2].ticketsGetSuccess : 'no data' },
        ]
      : [
          { 'action': t('dashboardPage.createAction'), 'time': actionList[0] ? actionList[0].ticketCreateFail : 'no data' },
          { 'action': t('dashboardPage.updateAction'), 'time': actionList[1] ? actionList[1].ticketUpdateFail : 'no data' },
          { 'action': t('dashboardPage.collectionAction'), 'time': actionList[2] ? actionList[2].ticketsGetFail : 'no data' },
        ]
    return renderList
  }

  const getCommentActionList = (items, type) => {
    const actionList = items ? (type ? items.slice(0, 1) : items.slice(1, 2)) : {}

    const renderList = type
      ? [
          { 'action': t('dashboardPage.createAction'), 'time': actionList[0] ? actionList[0].commentsCreateSuccess : 'no data' },
        ]
      : [
          { 'action': t('dashboardPage.createAction'), 'time': actionList[0] ? actionList[0].commentsCreateFail : 'no data' },
        ]
    return renderList
  }

  const getAttActionList = (items, type) => {
    const actionList = items ? (type ? items.slice(0, 1) : items.slice(1, 2)) : {}

    const renderList = type
      ? [
          { 'action': t('dashboardPage.createAction'), 'time': actionList[0] ? actionList[0].attachmentsCreateSuccess : 'no data' },
        ]
      : [
          { 'action': t('dashboardPage.createAction'), 'time': actionList[0] ? actionList[0].attachmentsCreateFail : 'no data' },
        ]
    return renderList
  }

  return (
    <MainWrap pageTitle={t('dashboardPage.pageTitle')}>
      <Content noPadding>
        <CardsListPanel
          onSelect={onTicketingSystemSelected}
          cards={ticketingSystemList}
          selectedCardId={selectedTicketingSystem.id}
          withUserbox
        />
        {selectedTicketingSystemDisplayId && (
          <ChartsWrapper data-test='dashboard-charts'>
            <TimeIntervalsPanel
              selectDashboardTimeInterval={onTimeIntervalSelected}
              dashboardSelectedTimeIntervalId={selectedTimeIntervalId}
            />

            <StatsPanel
              ticketingSystemDisplayId={selectedTicketingSystemDisplayId}
              isSummaryTab={isSummaryTab}
              colorizedBondedUnbondedTickets={colorizedBondedUnbondedTickets}
              colorizedTicketStatuses={colorizedTicketStatuses}
              recentBondedTicketsCount={recentBondedTicketsCount}
              recentUnbondedTicketsCount={recentUnbondedTicketsCount}
              recentNewTicketsCount={recentNewTicketsCount}
              recentClosedTicketsCount={recentClosedTicketsCount}
              fetchingBondedUnbonded={fetchingBondedUnbonded}
              fetchingOpenClosed={fetchingOpenClosed}
              fetchingRecentBondedUnbonded={fetchingRecentBondedUnbonded}
              fetchingRecentNewClosed={fetchingRecentNewClosed}
            />

            {!isSummaryTab && (
              <ChartsRow>
                <TicketStatusesChart
                  userTimeZoneAbbr={userTimeZoneAbbr}
                  userTimeFormat24={userTimeFormat24}
                  chartType='column'
                  ticketStatuses={colorizedTicketStatuses}
                  isFetching={fetchingOpenClosed}
                />
              </ChartsRow>
            )}

            <SlimChartRow data-test='dashboard-chart-row'>
              <BondedUnbondedCharts
                isSummaryTab={isSummaryTab}
                userTimeZoneAbbr={userTimeZoneAbbr}
                userTimeFormat24={userTimeFormat24}
                bondedUnbondedTickets={colorizedBondedUnbondedTickets}
                recentUnbondedTicketsCount={recentUnbondedTicketsCount}
                recentBondedTicketsCount={recentBondedTicketsCount}
                fetchingBondedUnbonded={fetchingBondedUnbonded}
                fetchingRecentBondedUnbonded={fetchingRecentBondedUnbonded}
              />
              {isSummaryTab && (
                <CreatedClosedChart
                  recentNewTicketsCount={recentNewTicketsCount}
                  recentClosedTicketsCount={recentClosedTicketsCount}
                  isFetching={fetchingRecentNewClosed}
                />
              )}
              {isSummaryTab && (
                <HttpRequestCodeChart
                  ticketingSystemDisplayId={selectedTicketingSystemDisplayId}
                />
              )}

              {!isSummaryTab && (
                <ResponseCodesChart
                  ticketingSystemDisplayId={selectedTicketingSystemDisplayId}
                  selectedTimeIntervalId={selectedTimeIntervalId}
                  userTimeZoneAbbr={userTimeZoneAbbr}
                  userTimeFormat24={userTimeFormat24}
                />
              )}

              {isSummaryTab && (
                <>
                  <Spacer />
                  <CartDiv>
                    <TicketsTable
                      ticketingSystem={selectedTicketingSystem}
                    />
                  </CartDiv>
                </>
              )}
            </SlimChartRow>

            {isSummaryTab && (
              <ChartsRow>
                <DivBlock>
                  <StatusBlocks>
                    <LastRequestStatusTable
                      title={t('dashboardPage.lastTicketSuccess')}
                      fetching={fetchingLastRequestStatuses}
                      userTimeZone={userTimeZone}
                      items={getTicketActionList(ticketLastRequestStatuses, true)}
                      type='success'
                      tooltipText={t('dashboardPage.latestRequestStatusTooltipText')}
                      tooltipId='last-ticket-success-request-tooltip'
                    />
                    <Spacer />
                    <LastRequestStatusTable
                      title={t('dashboardPage.lastTicketFail')}
                      fetching={fetchingLastRequestStatuses}
                      userTimeZone={userTimeZone}
                      items={getTicketActionList(ticketLastRequestStatuses, false)}
                      tooltipText={t('dashboardPage.latestFailRequestTooltipText')}
                      tooltipId='last-ticket-fail-request-tooltip'
                    />
                  </StatusBlocks>

                  <StatusBlocks>
                    <LastRequestStatusTable
                      title={t('dashboardPage.lastCommentSuccess')}
                      type='success'
                      fetching={fetchingLastRequestStatuses}
                      userTimeZone={userTimeZone}
                      items={getCommentActionList(commentLastRequestStatuses, true)}
                      tooltipText={t('dashboardPage.latestCommentSuccessTooltipText')}
                      tooltipId='last-comment-success-request-tooltip'
                    />
                    <Spacer />
                    <LastRequestStatusTable
                      title={t('dashboardPage.lastCommentFail')}
                      fetching={fetchingLastRequestStatuses}
                      userTimeZone={userTimeZone}
                      items={getCommentActionList(commentLastRequestStatuses, false)}
                      tooltipText={t('dashboardPage.latestCommentFailTooltipText')}
                      tooltipId='last-comment-fail-request-tooltip'
                    />
                  </StatusBlocks>
                  {showAttachmentsData && (
                    <StatusBlocks>
                      <LastRequestStatusTable
                        title={t('dashboardPage.lastAttachmentSuccess')}
                        type='success'
                        fetching={fetchingLastRequestStatuses}
                        userTimeZone={userTimeZone}
                        items={getAttActionList(attachmentLastRequestStatuses, true)}
                        tooltipText={t('dashboardPage.latestAttachmentSuccessTooltipText')}
                        tooltipId='last-attachment-success-request-tooltip'
                      />
                      <Spacer />
                      <LastRequestStatusTable
                        title={t('dashboardPage.lastAttachmentFail')}
                        fetching={fetchingLastRequestStatuses}
                        userTimeZone={userTimeZone}
                        items={getAttActionList(attachmentLastRequestStatuses, false)}
                        tooltipText={t('dashboardPage.latestAttachmentFailTooltipText')}
                        tooltipId='last-attachment-fail-request-tooltip'
                      />
                    </StatusBlocks>
                  )}
                </DivBlock>
                <Spacer />
                <Spacer />
                <StyledTableDiv>
                  <ApiRequestsTable
                    ticketingSystemDisplayId={selectedTicketingSystemDisplayId}
                    userTimeZone={userTimeZone}
                  />
                  <SpacerHeight />
                  <LastSubscriptionsTable
                    ticketingSystemDisplayId={selectedTicketingSystemDisplayId}
                    userTimeZone={userTimeZone}
                  />
                </StyledTableDiv>
              </ChartsRow>
            )}
          </ChartsWrapper>
        )}
      </Content>
    </MainWrap>
  )
}

export default DashboardPage
